import { useState, useEffect, useCallback } from "react";
import { Plus, Trash2, Edit, Save, Briefcase, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { api } from "./adminApi";
import { invalidateJobPreferencesCache } from "@/hooks/useJobPreferences";

export default function JobPreferencesTab() {
  const [prefs, setPrefs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ key: "", label: "", order: 99 });
  const [editingKey, setEditingKey] = useState(null);
  const [editLabel, setEditLabel] = useState("");

  const fetchPrefs = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/job-preferences");
      const sorted = [...r.data].sort((a, b) => (a.order || 99) - (b.order || 99));
      setPrefs(sorted);
      invalidateJobPreferencesCache(sorted);
    } catch { toast.error("Impossible de charger les préférences"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchPrefs(); }, [fetchPrefs]);

  const handleCreate = async () => {
    if (!form.label.trim()) { toast.error("Le libellé est requis"); return; }
    const key = form.key.trim() || form.label.trim().toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");
    try {
      await api.post("/admin/job-preferences", { key, label: form.label.trim(), order: parseInt(form.order) || 99 });
      toast.success("Préférence créée");
      setForm({ key: "", label: "", order: 99 });
      setShowForm(false);
      fetchPrefs();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  const handleUpdate = async (key) => {
    if (!editLabel.trim()) { toast.error("Libellé requis"); return; }
    try {
      await api.patch(`/admin/job-preferences/${key}`, { label: editLabel.trim() });
      toast.success("Mis à jour");
      setEditingKey(null);
      fetchPrefs();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  const handleDelete = async (key, label) => {
    if (!window.confirm(`Supprimer la préférence "${label}" ?\n\nLes candidats ayant déjà coché cette préférence la conserveront en base, mais elle ne sera plus proposée.`)) return;
    try {
      await api.delete(`/admin/job-preferences/${key}`);
      toast.success("Supprimée");
      fetchPrefs();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  const handleReorder = async (key, newOrder) => {
    try {
      await api.patch(`/admin/job-preferences/${key}`, { order: newOrder });
      fetchPrefs();
    } catch { toast.error("Erreur"); }
  };

  return (
    <div data-testid="job-preferences-tab" className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">{prefs.length} préférence(s) métier configurée(s)</p>
        <Button onClick={() => setShowForm((v) => !v)} className="bg-gradient-to-r from-[#662483] to-[#0069B4] text-white rounded-none font-semibold" data-testid="add-job-pref-btn">
          {showForm ? <X className="w-4 h-4 mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
          {showForm ? "Annuler" : "Nouvelle préférence"}
        </Button>
      </div>

      {showForm && (
        <div className="bg-white border border-gray-200 p-4 grid sm:grid-cols-3 gap-3 items-end" data-testid="job-pref-form">
          <div>
            <Label className="text-xs text-gray-500 mb-1 block">Libellé *</Label>
            <Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="ex. Agent de quai" className="rounded-none text-sm" data-testid="job-pref-form-label" />
          </div>
          <div>
            <Label className="text-xs text-gray-500 mb-1 block">Clé technique (optionnel)</Label>
            <Input value={form.key} onChange={(e) => setForm({ ...form, key: e.target.value })} placeholder="auto si vide" className="rounded-none text-sm" data-testid="job-pref-form-key" />
          </div>
          <div className="flex gap-2">
            <div className="flex-1">
              <Label className="text-xs text-gray-500 mb-1 block">Ordre</Label>
              <Input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} className="rounded-none text-sm" data-testid="job-pref-form-order" />
            </div>
            <Button onClick={handleCreate} className="bg-[#0069B4] text-white rounded-none font-semibold mb-px" data-testid="job-pref-form-save">
              <Save className="w-4 h-4 mr-2" /> Créer
            </Button>
          </div>
        </div>
      )}

      {loading ? (
        <p className="text-center py-8 text-gray-500">Chargement…</p>
      ) : prefs.length === 0 ? (
        <div className="bg-white border border-gray-200 p-8 text-center">
          <Briefcase className="w-8 h-8 mx-auto mb-2 text-gray-300" />
          <p className="text-sm text-gray-500">Aucune préférence configurée. Créez-en une pour permettre aux candidats de filtrer leurs métiers.</p>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 divide-y divide-gray-100">
          {prefs.map((p, idx) => (
            <div key={p.key} data-testid={`job-pref-${p.key}`} className="p-3 flex items-center gap-3">
              <span className="w-8 text-center text-xs font-mono text-gray-400">#{p.order || 99}</span>
              <div className="flex-1 min-w-0">
                {editingKey === p.key ? (
                  <Input value={editLabel} onChange={(e) => setEditLabel(e.target.value)} className="rounded-none text-sm h-8" autoFocus data-testid={`job-pref-edit-input-${p.key}`} />
                ) : (
                  <>
                    <p className="text-sm font-semibold text-[#3F3F3F]">{p.label}</p>
                    <p className="text-[10px] font-mono text-gray-400">{p.key}</p>
                  </>
                )}
              </div>
              <div className="flex gap-1 shrink-0">
                {idx > 0 && (
                  <button onClick={() => handleReorder(p.key, (p.order || 99) - 1)} className="p-2 hover:bg-gray-100 text-gray-500 text-xs" title="Monter" data-testid={`job-pref-up-${p.key}`}>↑</button>
                )}
                {idx < prefs.length - 1 && (
                  <button onClick={() => handleReorder(p.key, (p.order || 99) + 1)} className="p-2 hover:bg-gray-100 text-gray-500 text-xs" title="Descendre" data-testid={`job-pref-down-${p.key}`}>↓</button>
                )}
                {editingKey === p.key ? (
                  <>
                    <button onClick={() => handleUpdate(p.key)} className="p-2 hover:bg-gray-100 text-green-600" title="Sauvegarder" data-testid={`job-pref-save-${p.key}`}><Save className="w-4 h-4" /></button>
                    <button onClick={() => setEditingKey(null)} className="p-2 hover:bg-gray-100 text-gray-500" title="Annuler"><X className="w-4 h-4" /></button>
                  </>
                ) : (
                  <button onClick={() => { setEditingKey(p.key); setEditLabel(p.label); }} className="p-2 hover:bg-gray-100 text-[#0069B4]" title="Renommer" data-testid={`job-pref-edit-${p.key}`}><Edit className="w-4 h-4" /></button>
                )}
                <button onClick={() => handleDelete(p.key, p.label)} className="p-2 hover:bg-gray-100 text-red-500" title="Supprimer" data-testid={`job-pref-delete-${p.key}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
